import React, { useState } from "react";
import {
  faTimes,
  faPaperclip,
  faFilePdf,
  faFileWord,
  faFileExcel,
  faFileImage,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

const UploadInvoiceModal = ({
  uploadInvoicePopup,
  setUploadInvoicePopup,
  order,
  bill,
  onSuccess,
}) => {
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [attachment, setAttachment] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  const handlePickFile = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const allowed = [
      "pdf",
      "doc",
      "docx",
      "xls",
      "xlsx",
      "png",
      "jpg",
      "jpeg",
      "gif",
      "txt",
    ];

    const ext = file.name.split(".").pop().toLowerCase();

    if (!allowed.includes(ext)) {
      alert("Invalid file type. Allowed: PDF, DOC, XLS, Images");
      return;
    }

    setAttachment({
      file,
      name: file.name,
      type: file.type,
      sizeMB: (file.size / (1024 * 1024)).toFixed(2),
    });
  };

  const getFileIcon = (fileName) => {
    const ext = fileName.split(".").pop().toLowerCase();
    if (ext === "pdf") return faFilePdf;
    if (["doc", "docx"].includes(ext)) return faFileWord;
    if (["xls", "xlsx"].includes(ext)) return faFileExcel;
    if (["png", "jpg", "jpeg", "gif"].includes(ext)) return faFileImage;
    return faPaperclip;
  };

  const getFileColor = (fileName) => {
    const ext = fileName.split(".").pop().toLowerCase();
    if (ext === "pdf") return "#FF0000";
    if (["doc", "docx"].includes(ext)) return "#2B579A";
    if (["xls", "xlsx"].includes(ext)) return "#217346";
    if (["png", "jpg", "jpeg", "gif"].includes(ext)) return "#FF6B35";
    return "#666";
  };

  const handleSubmit = async () => {
    if (!invoiceNumber.trim()) {
      alert("Please enter invoice number");
      return;
    }

    if (!attachment) {
      alert("Please attach a file");
      return;
    }

    try {
      setIsUploading(true);

      const vendorData = JSON.parse(localStorage.getItem("vendorData") || "{}");
      const token = vendorData.token;

      if (!token) {
        alert("Authentication failed. Login again.");
        return;
      }

      const formData = new FormData();
      formData.append("files", attachment.file);
      formData.append("billId", bill._id);
      formData.append(
        "updateData",
        JSON.stringify({
          bill_number: invoiceNumber,
        })
      );

      const response = await fetch(
        `${process.env.REACT_APP_FORE_BRICKBUCKET}bill/update-bill`,
        {
          method: "PUT",
          headers: {
            "x-token": token,
          },
          body: formData,
        }
      );

      const result = await response.json();

      if (!result.success) throw new Error(result.message);

      alert("Invoice uploaded successfully!");
      setUploadInvoicePopup(false);
      setAttachment(null);
      setInvoiceNumber("");
      onSuccess?.();
    } catch (err) {
      alert(err.message || "Upload failed");
    } finally {
      setIsUploading(false);
    }
  };

  if (!uploadInvoicePopup || !order || !bill) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex justify-end z-50">
      <div className="w-full bg-white rounded-t-3xl max-h-[90%] overflow-y-auto p-5">

        {/* Header */}
        <div className="flex justify-between items-center border-b pb-3 mb-3">
          <h2 className="text-xl font-bold">Upload Invoice</h2>
          <button
            className="text-gray-600"
            onClick={() => setUploadInvoicePopup(false)}
          >
            <FontAwesomeIcon icon={faTimes} size="lg" />
          </button>
        </div>

        {/* BILL TO */}
        <h3 className="font-semibold mb-1">Bill To</h3>
        <div className="bg-gray-100 p-3 rounded-lg mb-3">
          <p className="font-semibold">{bill.billing_address?.name}</p>
          <p>{bill.billing_address?.address}</p>
          {bill.billing_address?.gstin && (
            <p>GST: {bill.billing_address.gstin}</p>
          )}
        </div>

        {/* SHIP TO */}
        <h3 className="font-semibold mb-1">Ship To</h3>
        <div className="bg-gray-100 p-3 rounded-lg mb-3">
          <p className="font-semibold">{bill.shipping_address?.name}</p>
          <p>{bill.shipping_address?.address}</p>
          {bill.shipping_address?.gstin && (
            <p>GST: {bill.shipping_address.gstin}</p>
          )}
        </div>

        {/* Invoice Number */}
        <label className="font-semibold">Invoice Number *</label>
        <input
          className="w-full p-3 border rounded-xl mt-1 mb-4"
          placeholder="Enter invoice number"
          value={invoiceNumber}
          onChange={(e) => setInvoiceNumber(e.target.value)}
          disabled={isUploading}
        />

        {/* File Upload */}
        <label className="font-semibold">Attach Invoice *</label>

        {!attachment ? (
          <div className="border-2 border-dashed p-8 rounded-xl text-center bg-gray-50 mt-2 mb-4">
            <FontAwesomeIcon icon={faPaperclip} size="2x" className="mb-3 text-gray-600" />

            <p className="font-medium text-gray-700">Click to upload</p>
            <p className="text-sm text-gray-500">PDF, DOC, XLS, Images</p>

            <input
              type="file"
              className="hidden"
              id="invoiceFile"
              onChange={handlePickFile}
              disabled={isUploading}
            />
            <label
              htmlFor="invoiceFile"
              className="cursor-pointer block mt-3 text-blue-600 underline"
            >
              Browse File
            </label>
          </div>
        ) : (
          <div className="p-4 bg-gray-50 border rounded-xl mb-4 flex items-center">
            <FontAwesomeIcon
              icon={getFileIcon(attachment.name)}
              size="lg"
              color={getFileColor(attachment.name)}
              className="mr-3"
            />
            <div className="flex-1">
              <p className="font-semibold truncate">{attachment.name}</p>
              <p className="text-sm text-gray-600">{attachment.sizeMB} MB</p>
            </div>
            <button onClick={() => setAttachment(null)}>
              <FontAwesomeIcon icon={faTrash} size="lg" color="#EF4444" />
            </button>
          </div>
        )}

        {/* Buttons */}
        <div className="flex gap-3 mt-5">
          <button
            className="flex-1 border py-3 rounded-xl"
            onClick={() => setUploadInvoicePopup(false)}
            disabled={isUploading}
          >
            Cancel
          </button>

          <button
            className={`flex-1 py-3 rounded-xl text-white ${
              isUploading ? "bg-red-300" : "bg-red-500"
            }`}
            onClick={handleSubmit}
            disabled={isUploading}
          >
            {isUploading ? "Uploading..." : "Submit Invoice"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UploadInvoiceModal;
